import { apiRequest } from 'platform/utilities/api';
import environment from 'platform/utilities/environment';
import ADDRESS_DATA from 'platform/forms/address/data';
import { showAddressValidationModal } from 'platform/user/profile/vap-svc/util';

const CONFIRMED = 'CONFIRMED';

// snake_case API keys as consts (computed properties) to satisfy the
// camelcase lint rule — same approach as the aquia implementation.
const ADDRESS_LINE1 = 'address_line1';
const ADDRESS_LINE2 = 'address_line2';
const ADDRESS_LINE3 = 'address_line3';
const ADDRESS_POU = 'address_pou';
const ADDRESS_TYPE = 'address_type';
const COUNTRY_CODE_ISO3 = 'country_code_iso3';
const STATE_CODE = 'state_code';
const PROVINCE = 'province';
const ZIP_CODE = 'zip_code';
const INTERNATIONAL_POSTAL_CODE = 'international_postal_code';

const isUSA = address => (address.country || 'USA') === 'USA';

const isMilitaryAddress = address =>
  Boolean(address.isMilitary) ||
  ADDRESS_DATA.militaryStates.includes(address.state);

/**
 * Converts an addressUI/addressSchema form-data address
 * (street/street2/city/state/postalCode/country/isMilitary) into the
 * snake_case payload for POST /v0/profile/address_validation.
 *
 * Note: the endpoint's strong params accept `international_postal_code`
 * (not `int_postal_code`) — see vets-api
 * app/controllers/v0/profile/address_validation_controller.rb.
 *
 * @param {Object} address - addressUI-shaped form data address
 * @returns {Object} API-formatted address
 */
export const prepareAddressForApi = address => {
  const country = address.country || 'USA';
  const domestic = isUSA(address);
  let addressType = 'INTERNATIONAL';
  if (domestic) {
    addressType = isMilitaryAddress(address) ? 'OVERSEAS MILITARY' : 'DOMESTIC';
  }

  return {
    [ADDRESS_LINE1]: address.street,
    [ADDRESS_LINE2]: address.street2 || undefined,
    [ADDRESS_LINE3]: address.street3 || undefined,
    [ADDRESS_POU]: 'CORRESPONDENCE',
    [ADDRESS_TYPE]: addressType,
    city: address.city,
    [COUNTRY_CODE_ISO3]: country,
    ...(domestic
      ? { [STATE_CODE]: address.state, [ZIP_CODE]: address.postalCode }
      : {
          [PROVINCE]: address.state,
          [INTERNATIONAL_POSTAL_CODE]: address.postalCode,
        }),
  };
};

// Maps an API suggestion back into the addressUI form-data shape so it can be
// written into formData at the same path the address page uses. The user's
// address is spread first so non-address keys the form stores alongside it
// (isMilitary, view: fields like view:militaryBaseDescription) are preserved;
// every address component is then explicitly replaced by the suggestion's.
const mapSuggestionToFormAddress = (suggestion, userAddress) => ({
  ...userAddress,
  street: suggestion.addressLine1,
  street2: suggestion.addressLine2 || undefined,
  street3: suggestion.addressLine3 || undefined,
  city: suggestion.city,
  country: suggestion.countryCodeIso3,
  state: suggestion.stateCode || suggestion.province,
  postalCode: suggestion.zipCode || suggestion.internationalPostalCode,
});

const isUsableSuggestion = ({ address, addressMetaData }) =>
  Boolean(address) &&
  (addressMetaData?.deliveryPointValidation === CONFIRMED ||
    addressMetaData?.addressType?.toLowerCase() === 'international');

/**
 * Calls the address validation endpoint and applies the platform-standard
 * decision policy (the same heuristic the VA.gov Profile flow uses via
 * showAddressValidationModal): the suggestion screen is skipped only when the
 * API returns a single confirmed, high-confidence (> 90) suggestion in the
 * same state with no street2/street3 in play.
 *
 * Errors are swallowed by design — an outage must never block form progress —
 * and reported via the `error` flag for optional messaging.
 *
 * @param {Object} userAddress - addressUI-shaped form data address
 * @returns {Promise<{
 *   suggestions: Array<{formAddress: Object, confidenceScore: number, deliveryPointValidation: string}>,
 *   showSuggestions: boolean,
 *   confidenceScore: number|null,
 *   error?: boolean,
 * }>}
 */
export const fetchAddressSuggestions = async userAddress => {
  const options = {
    body: JSON.stringify({ address: prepareAddressForApi(userAddress) }),
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  };

  try {
    const res = await apiRequest(
      `${environment.API_URL}/v0/profile/address_validation`,
      options,
    );

    const allSorted = (res?.addresses || [])
      .filter(({ address }) => Boolean(address))
      .sort(
        (a, b) =>
          (b.addressMetaData?.confidenceScore ?? 0) -
          (a.addressMetaData?.confidenceScore ?? 0),
      );
    const usable = allSorted.filter(isUsableSuggestion);

    if (!usable.length) {
      return { suggestions: [], showSuggestions: false, confidenceScore: null };
    }

    const confidenceScore = usable[0].addressMetaData?.confidenceScore ?? null;

    // ...but the skip-the-picker decision uses the UNFILTERED list, matching
    // the profile flow: the picker is only skipped when one and only one
    // suggestion came back at all and it passed every check. Filtering first
    // would silently auto-apply a good suggestion whenever USPS also returned
    // uncertain ones.
    const showSuggestions = showAddressValidationModal(
      allSorted.map(({ address, addressMetaData }) => ({
        ...address,
        addressMetaData,
      })),
      {
        addressLine2: userAddress.street2,
        addressLine3: userAddress.street3,
        stateCode: userAddress.state,
      },
    );

    return {
      suggestions: usable.map(({ address, addressMetaData }) => ({
        formAddress: mapSuggestionToFormAddress(address, userAddress),
        confidenceScore: addressMetaData?.confidenceScore ?? null,
        deliveryPointValidation: addressMetaData?.deliveryPointValidation,
      })),
      showSuggestions,
      confidenceScore,
    };
  } catch {
    return {
      suggestions: [],
      showSuggestions: false,
      confidenceScore: null,
      error: true,
    };
  }
};
